import {
  ArrowForward,
  CheckCircleOutlineOutlined,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  TimerOffOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Chip,
  IconButton,
  Paper,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import * as dayjs from "dayjs";
import api from "../../../services/api";
import { ptBR } from "@mui/x-data-grid/locales";
import { ReactComponent as EmptyBox } from "../../../assets/easyReport/iconSvg/emptyBox.svg";
import { AgendamentoRelatorio } from "../../../pages/easyReport/agendamento/agendamenoDetalhado";

const stylesPage = (theme: Theme) => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
    borderRadius: 2,
  },
  dataGrid: {
    width: "100%",
    height: "100%",
    minHeight: 500,
    fontSize: 12,
    borderRadius: 0,
    border: 0,
    backgroundColor: "transparent",
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    ".MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
      borderColor: theme.palette.stroke,
    },
    "& .MuiDataGrid-scrollbar--vertical": {
      display: "none",
    },
    "& .MuiDataGrid-filler": {
      display: "none",
    },
    "& .MuiDataGrid-scrollbarFiller--borderTop": {
      display: "none",
    },
    "& .MuiDataGrid-scrollbarFiller--header": {
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.stroke}`,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      color: "text.primary", // Altera a cor do titulo do cabeçalho
    },
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: "transparent", // Altera cor do rodapé
      border: 0,
      borderTop: `1px solid ${theme.palette.stroke}`,
      height: "45px",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: theme.palette.background.paper, // Altera cor do cabeçalho
      border: 0,
      borderTop: `1px solid ${theme.palette.stroke}`,
      width: "100%",
    },
    "& .MuiDataGrid-topContainer": {
      border: 0,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiDataGrid-row--borderBottom": {
      border: 0,
    },
    "& .MuiDataGrid-columnHeaders": {
      border: 0,
      height: "55px",
      backgroundColor: theme.palette.background.paper,
    },

    "& .even-row": { backgroundColor: theme.palette.table },
    "& .odd-row": { backgroundColor: theme.palette.background.paper },
  },
  boxRenderCell: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
    alignItems: "center",
    gap: 1,
  },
  typography: {
    fontSize: "0.8rem",
  },
  chip: {
    borderRadius: "7px",
    fontWeight: 500,
    color: theme.palette.background.card,
    backgroundColor: alpha(theme.palette.background.cardInverted, 0.6),
    padding: "0px 8px",
    height: "28px",
    maxWidth: "100px",
    marginRight: "4px",
    "& .MuiChip-label": {
      padding: 0,
    },
  },
  boxNoRowsOverlay: {
    p: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  iconUserNotFound: {
    width: "140px",
    height: "140px",
    fill: theme.palette.text.disabled,
  },
  containerColumnLastLogin: {
    display: "flex",
    justifyContent: "start",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 0.5,
    height: "100%",
  },
  textDate: {
    color: "text.primary",
    lineHeight: 1.5,
  },
  boxRenderCellIcon: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
  },
});
interface AgendamentoRelatoriosProps {
  id?: string | null;
  searchValue?: string;
}

export default function AgendamentoRelatorios({
  id = null,
  searchValue = "",
}: AgendamentoRelatoriosProps) {
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AgendamentoRelatorio[]>([]);
  const theme = useTheme();
  const styles = stylesPage(theme);

  useEffect(() => {
    const fetchAppointmentReports = async () => {
      try {
        setLoading(true);

        const res = await api.post("/easyReport/v2/findManySchedulesReport");

        const filteredRelatorios = res.data.filter(
          (relatorio: AgendamentoRelatorio) => relatorio.status === 1,
        );
        setRows(filteredRelatorios);
      } catch (error) {
        console.error("Erro ao buscar relatórios:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAppointmentReports();
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!searchValue && !id) return true;

      if (id && row?.agendamentoId !== id) return false;

      if (searchValue) {
        return (
          row?.titulo?.toLowerCase().includes(searchValue) ||
          row.items.some((item) => item.toLowerCase().includes(searchValue))
        );
      }

      return true;
    });
  }, [id, rows, searchValue]);

  const columns: GridColDef<AgendamentoRelatorio>[] = [
    {
      field: "titulo",
      headerName: "Agendamento",
      sortable: false,
      width: 120,
    },
    {
      field: "intervaloColeta",
      headerName: "Intervalo de Coleta",
      sortable: false,
      width: 240,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataInicialColeta).format("DD/MM/YYYY")}
          </Typography>
          <ArrowForward
            sx={{ fontSize: "1.1rem", color: theme.palette.text.primary }}
          />
          <Typography sx={styles.typography}>
            {dayjs(params.row.dataGeracao).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "profiles",
      headerName: "Perfil de Relatório",
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCell}>
          {Array.isArray(params.value) &&
            params.value.map((perfil) => (
              <Tooltip title={perfil}>
                <Chip
                  key={perfil}
                  label={perfil}
                  sx={{
                    ...styles.chip,
                    borderRadius: "7px",
                    maxWidth: "100px",
                  }}
                />
              </Tooltip>
            ))}
        </Box>
      ),
    },
    {
      field: "hosts",
      headerName: "Hosts",
      minWidth: 200,
      flex: 2,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const value = params.value || [];
        const display = value.join(", ");
        return (
          <Tooltip title={display}>
            <Box
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {display}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "items",
      headerName: "Itens",
      minWidth: 300,
      flex: 3,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        return (
          <Box sx={styles.boxRenderCell}>
            {params.value?.map((item: string) => (
              <Tooltip title={item}>
                <Chip sx={styles.chip} label={item} key={item} />
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 60,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {params.row.status === 1 ? (
            <Tooltip title="Relatório Gerado">
              <CheckCircleOutlineOutlined color="success" />
            </Tooltip>
          ) : params.row.status === 2 ? (
            <Tooltip title="Erro ao gerar relatório">
              <ErrorOutlineOutlined color="error" />
            </Tooltip>
          ) : params.row.status === 0 ? (
            <Tooltip title="Relatório Não gerado">
              <TimerOutlined />
            </Tooltip>
          ) : (
            <Tooltip title="Relatório ultrapassou data">
              <TimerOffOutlined sx={{ color: theme.palette.text.disabled }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "etapa",
      headerName: "Etapa",
      width: 60,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => (
        <Box sx={styles.boxRenderCellIcon}>
          {`${params.row.etapa}/${params.row.ocorrencias}`}
        </Box>
      ),
    },
    {
      field: "visualizar",
      headerName: "Ações",
      headerAlign: "center",
      width: 85,
      renderCell: (params: GridRenderCellParams<AgendamentoRelatorio>) => {
        const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          try {
            const response = await api.post(
              "/easyReport/v2/downloadReport",
              { id: params.row.relatorioId },
              { responseType: "blob" },
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${params.row.titulo || "relatorio"}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Erro ao baixar relatório:", error);
          }
        };
        return (
          <Tooltip title="Visualizar">
            <Box sx={styles.boxRenderCellIcon}>
              <IconButton
                disabled={params.row.status !== 1}
                onClick={handleClick}
              >
                <DescriptionOutlined
                  color={params.row.status !== 1 ? "disabled" : "primary"}
                />
              </IconButton>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">
          Nenhum relatório gerado encontrado para este agendamento
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={styles.paper}>
      <DataGrid
        sx={styles.dataGrid}
        rows={filteredRows}
        columns={columns}
        loading={loading}
        disableColumnMenu={true}
        pageSizeOptions={[10, 25, 50, 100]}
        onPaginationModelChange={(model) => setPageSize(model.pageSize)}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: pageSize },
          },
        }}
      />
    </Paper>
  );
}
